import React, { useState, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import {
  PlusIcon,
  CalendarIcon,
  CalendarDaysIcon,
  ClockIcon,
  UsersIcon,
  ArrowPathRoundedSquareIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { ModalShell, ModalActions, ModalField, MODAL_INPUT, MODAL_TEXTAREA } from './modals/core';
import type { ModalCloseReason } from './modals/core';

/** Kleines Label-Icon (16px, gedämpft) im Entwurf-Stil. */
const labelIconClass = 'h-4 w-4 text-gray-500 dark:text-gray-400';

/** Serifen-„T" als Label-Icon für das Titel-Feld (wie im Design-Entwurf). */
const TitleIcon: React.FC = () => (
  <span className="w-4 text-center font-serif text-[15px] font-semibold leading-none text-gray-500 dark:text-gray-400">
    T
  </span>
);

/** Zusätzliche linke Innen-Polsterung, wenn ein Icon im Feld sitzt. */
const MODAL_INPUT_WITH_ICON = `${MODAL_INPUT} pl-10`;

/** Umschließt ein Eingabefeld und blendet links ein Icon ein. */
const FieldIcon: React.FC<{ icon: React.ComponentType<{ className?: string }>; children: React.ReactNode }> = ({
  icon: Icon,
  children,
}) => (
  <div className="relative">
    <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
    {children}
  </div>
);

interface AddCalendarEventProps {
  selectedDate: Date | null;
  onAddEvent: (event: any) => void;
  refreshCalendar: () => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const AddCalendarEvent: React.FC<AddCalendarEventProps> = ({ selectedDate, onAddEvent, refreshCalendar, isOpen, onClose }) => {
  const { user } = useContext(AuthContext);
  const [isModalOpen, setIsModalOpen] = useState(isOpen || false);
  const [loading, setLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  React.useEffect(() => {
    if (isOpen !== undefined) {
      setIsModalOpen(isOpen);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setIsModalOpen(false);
    }
    resetForm();
  };

  const handleModalClose = (_reason: ModalCloseReason) => {
    handleClose();
  };

  const [formData, setFormData] = useState({
    title: '',
    date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    startTime: '18:00',
    endTime: '19:00',
    color: '#3B82F6',
    bookable: 0,
    repeat: 'none',
    repeatEnd: '',
    cotrainerId: '',
    taetigkeitsbericht: false,
    taetigkeitsberichtText: ''
  });

  // Update date when selectedDate changes
  React.useEffect(() => {
    if (selectedDate) {
      setFormData(prev => ({
        ...prev,
        date: format(selectedDate, 'yyyy-MM-dd')
      }));
    }
  }, [selectedDate]);

  const eventColors = [
    { value: '#3B82F6', label: 'Blau' },
    { value: '#10B981', label: 'Grün' },
    { value: '#F59E0B', label: 'Orange' },
    { value: '#EF4444', label: 'Rot' },
    { value: '#8B5CF6', label: 'Violett' },
    { value: '#EC4899', label: 'Pink' },
    { value: '#6B7280', label: 'Grau' }
  ];

  // Helper function to add 1 hour to a time string
  const addOneHour = (timeStr: string): string => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const newHours = (hours + 1) % 24;
    return `${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  // Handle start time change
  const handleStartTimeChange = (newStartTime: string) => {
    const newEndTime = addOneHour(newStartTime);
    setFormData({ ...formData, startTime: newStartTime, endTime: newEndTime });
  };

  // Add one week to date
  const addOneWeek = (dateStr: string): string => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + 7);
    return format(date, 'yyyy-MM-dd');
  };

  // Handle repeat change
  const handleRepeatChange = (newRepeat: string) => {
    if (newRepeat !== 'none' && formData.repeatEnd === '') {
      const endDate = addOneWeek(formData.date);
      setFormData({ ...formData, repeat: newRepeat, repeatEnd: endDate });
    } else {
      setFormData({ ...formData, repeat: newRepeat });
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLoading(true);

    try {
      const eventData = {
        title: formData.title,
        start: formData.date,
        start_time: formData.startTime,
        end_time: formData.endTime,
        color: formData.color,
        bookable: formData.bookable,
        repeat: formData.repeat,
        repeat_end: formData.repeatEnd || null,
        cotrainer_id: formData.cotrainerId || null,
        taetigkeitsbericht: formData.taetigkeitsbericht ? 1 : 0,
        taetigkeitsbericht_text: formData.taetigkeitsbericht ? formData.taetigkeitsberichtText : null
      };

      const response = await apiClient.post('/api/calendar', eventData);

      if (response.data.success) {
        const newEvent = {
          id: response.data.event_id,
          title: formData.title,
          start: new Date(`${formData.date}T${formData.startTime}`),
          end: new Date(`${formData.date}T${formData.endTime}`),
          color: formData.color,
          bookable: formData.bookable,
          bookingCount: 0,
          creator: user?.vorname + ' ' + user?.nachname,
          creator_id: user?.mg_nr,
          repeat: formData.repeat,
          repeat_end: formData.repeatEnd,
          taetigkeitsbericht: formData.taetigkeitsbericht ? 1 : 0,
          taetigkeitsbericht_text: formData.taetigkeitsberichtText
        };

        onAddEvent(newEvent);
        refreshCalendar();
        handleClose();
      }
    } catch (error) {
      console.error('Error creating event:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      startTime: '18:00',
      endTime: '19:00',
      color: '#3B82F6',
      bookable: 0,
      repeat: 'none',
      repeatEnd: '',
      cotrainerId: '',
      taetigkeitsbericht: false,
      taetigkeitsberichtText: ''
    });
  };

  return (
    <>
      {/* Trigger-Button nur im unkontrollierten Modus (kein isOpen von außen) */}
      {isOpen === undefined && (
        <button
          onClick={() => setIsModalOpen(true)}
          className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          title="Termin hinzufügen"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      )}

      <ModalShell
        open={isModalOpen}
        onClose={handleModalClose}
        variant="form"
        size="md"
        title={formData.title.trim() || 'Neuen Termin erstellen'}
        icon={PlusIcon}
        accent="brand"
        closeOnBackdrop={false}
        footer={
          <ModalActions
            secondary={{ label: 'Abbrechen', onClick: handleClose, disabled: loading }}
            primary={{
              label: loading ? 'Erstellen...' : 'Erstellen',
              onClick: () => formRef.current?.requestSubmit(),
              loading,
              disabled: loading,
            }}
          />
        }
      >
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
          <ModalField label="Titel" htmlFor="ace-title" required icon={<TitleIcon />}>
            <input
              id="ace-title"
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className={MODAL_INPUT}
              placeholder="z.B. Training, Agility, Vorstandssitzung"
            />
          </ModalField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ModalField
              label="Datum"
              htmlFor="ace-date"
              required
              icon={<CalendarIcon className={labelIconClass} />}
            >
              <FieldIcon icon={CalendarIcon}>
                <input
                  id="ace-date"
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className={MODAL_INPUT_WITH_ICON}
                />
              </FieldIcon>
            </ModalField>

            <ModalField label="Farbe">
              <div className="flex h-[38px] items-center gap-2">
                {eventColors.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-105 ${
                      formData.color === color.value ? 'border-gray-900 dark:border-white' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                    aria-label={color.label}
                  />
                ))}
              </div>
            </ModalField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <ModalField
              label="Startzeit"
              htmlFor="ace-start"
              required
              icon={<ClockIcon className={labelIconClass} />}
            >
              <FieldIcon icon={ClockIcon}>
                <input
                  id="ace-start"
                  type="time"
                  required
                  value={formData.startTime}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                  className={MODAL_INPUT_WITH_ICON}
                />
              </FieldIcon>
            </ModalField>

            <ModalField
              label="Endzeit"
              htmlFor="ace-end"
              required
              icon={<ClockIcon className={labelIconClass} />}
            >
              <FieldIcon icon={ClockIcon}>
                <input
                  id="ace-end"
                  type="time"
                  required
                  value={formData.endTime}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                  className={MODAL_INPUT_WITH_ICON}
                />
              </FieldIcon>
            </ModalField>
          </div>

          <ModalField
            label="Max. Teilnehmer"
            htmlFor="ace-bookable"
            icon={<UsersIcon className={labelIconClass} />}
            hint={formData.bookable === 0 ? 'nicht buchbar' : `${formData.bookable} Plätze`}
          >
            <input
              id="ace-bookable"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={String(formData.bookable)}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setFormData({ ...formData, bookable: val === '' ? 0 : parseInt(val) });
              }}
              className={MODAL_INPUT}
            />
          </ModalField>

          <ModalField
            label="Wiederholung"
            htmlFor="ace-repeat"
            icon={<ArrowPathRoundedSquareIcon className={labelIconClass} />}
          >
            <FieldIcon icon={ArrowPathRoundedSquareIcon}>
              <select
                id="ace-repeat"
                value={formData.repeat}
                onChange={(e) => handleRepeatChange(e.target.value)}
                className={MODAL_INPUT_WITH_ICON}
              >
                <option value="none">Keine Wiederholung</option>
                <option value="weekly">Wöchentlich</option>
                <option value="biweekly">Alle 2 Wochen</option>
                <option value="monthly">Monatlich</option>
              </select>
            </FieldIcon>
          </ModalField>

          {formData.repeat !== 'none' && (
            <ModalField
              label="Wiederholung endet am"
              htmlFor="ace-repeatend"
              icon={<CalendarDaysIcon className={labelIconClass} />}
            >
              <FieldIcon icon={CalendarIcon}>
                <input
                  id="ace-repeatend"
                  type="date"
                  value={formData.repeatEnd}
                  onChange={(e) => setFormData({ ...formData, repeatEnd: e.target.value })}
                  min={formData.date}
                  className={MODAL_INPUT_WITH_ICON}
                />
              </FieldIcon>
            </ModalField>
          )}

          <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-gray-700">
            <label
              htmlFor="taetigkeitsbericht"
              className="flex items-center gap-3 text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              <input
                type="checkbox"
                id="taetigkeitsbericht"
                checked={formData.taetigkeitsbericht}
                onChange={(e) => setFormData({ ...formData, taetigkeitsbericht: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
              />
              Im Tätigkeitsbericht erwähnen
            </label>
            {formData.taetigkeitsbericht && (
              <ModalField
                label="Beschreibung für Tätigkeitsbericht"
                htmlFor="ace-tbtext"
                hint={`${formData.taetigkeitsberichtText.length}/500`}
              >
                <textarea
                  id="ace-tbtext"
                  value={formData.taetigkeitsberichtText}
                  onChange={(e) => setFormData({ ...formData, taetigkeitsberichtText: e.target.value })}
                  className={MODAL_TEXTAREA}
                  rows={2}
                  placeholder="z.B. Jahreshauptversammlung mit Vorstandswahlen"
                  maxLength={500}
                />
              </ModalField>
            )}
          </div>
        </form>
      </ModalShell>
    </>
  );
};

export default AddCalendarEvent;
